import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ExternalLink, ArrowLeft } from "lucide-react";

import RightDrawer from "../../shared/components/RightDrawer";
import EmployeeProfileCard from "./EmployeeProfileCard";
import StatusBadge from "../../shared/components/StatusBadge";


import ContractsTab from "../tabs/ContractsTab";
import ScheduleTab from "../tabs/ScheduleTab";
import HistoryTab from "../tabs/HistoryTab";

const EmployeeDrawer = ({
  employee,
  isOpen = false,
  open = false,
  onClose,

  contracts = [],
  schedules = [],
  history = [],
  employees = [],
}) => {
  const navigate = useNavigate();
  const drawerOpen = isOpen || open;

  const [activeTab, setActiveTab] =
    useState("overview");

  if (!drawerOpen) {
    return null;
  }

  const tabs = [
    {
      id: "overview",
      label: "Overview",
    },
    {
      id: "history",
      label: "History",
    },
    {
      id: "contracts",
      label: "Contracts",
    },
    {
      id: "schedule",
      label: "Schedule",
    },
  ];

  // Employee-specific data with robust fallback objects

  const fullName = employee
    ? employee.full_name || `${employee.first_name || ""} ${employee.last_name || ""}`.trim()
    : "Employee Details";

  const employeeContracts = contracts.filter(
    (contract) =>
      contract.employee_uuid === employee?.uuid ||
      contract.employee_id === employee?.employee_id
  );

  const activeContractsList = employeeContracts.length > 0 ? employeeContracts : [
    {
      uuid: employee?.uuid || "cntr-default",
      contract_number: employee?.contract_number || "CNTR-2026-001",
      employee_uuid: employee?.uuid,
      employee_name: fullName,
      contract_type: employee?.contract_type || "Permanent Agreement",
      start_date: employee?.start_date || employee?.joining_date || "2026-01-01",
      end_date: employee?.end_date || null,
      status: "Active",
      department_name: employee?.department_name || "Engineering",
    }
  ];

  const employeeHistory = history.filter(
    (item) => item.employee_uuid === employee?.uuid
  );

  const employeeSchedules = schedules.filter(
    (schedule) =>
      schedule.uuid === employee?.work_schedule_uuid ||
      schedule.schedule_name === employee?.work_schedule_name
  );

  const activeSchedulesList = employeeSchedules.length > 0 ? employeeSchedules : [
    {
      uuid: employee?.work_schedule_uuid || "sched-default",
      schedule_name: employee?.work_schedule_name || "General Shift (09:00 - 18:00)",
      shift_type: "Regular",
      work_days: "Monday - Friday",
      hours_per_day: 9,
      start_time: "09:00",
      end_time: "18:00",
      status: "Active",
    }
  ];

  const getInitials = (name = "") => {
    if (!name) return "EMP";
    const parts = name.trim().split(" ");
    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    return name.slice(0, 2).toUpperCase();
  };

  const getInitialsBg = (name = "") => {
    const colors = [
      "bg-blue-600",
      "bg-indigo-600",
      "bg-[#147db5]",
      "bg-emerald-600",
      "bg-purple-600",
      "bg-violet-600",
      "bg-teal-600",
      "bg-sky-600",
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  // Render

  return (
    <RightDrawer
      open={drawerOpen}
      onClose={onClose}
      title=""
      description=""
      width="560px"
    >
      <div className="flex h-full flex-col">
        {/* Profile Header */}
        {employee && (
          <div className="flex items-start gap-3.5 px-6 py-5 border-b border-slate-100">
            {/* Close Button */}
            <button
              type="button"
              onClick={onClose}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-500 transition hover:bg-slate-100 hover:text-slate-800 shadow-2xs mr-0.5 mt-1"
              title="Close drawer"
            >
              <ArrowLeft size={18} strokeWidth={2} />
            </button>

            <div className="relative shrink-0">
              {employee.avatar ? (
                <img
                  src={employee.avatar}
                  alt={fullName}
                  className="h-16 w-16 rounded-full object-cover border border-slate-100"
                />
              ) : (
                <div className={`flex h-16 w-16 items-center justify-center rounded-full text-lg font-bold text-white shadow-sm ${getInitialsBg(fullName)}`}>
                  {getInitials(fullName)}
                </div>
              )}
              <span className={`absolute -right-0.5 -top-0.5 h-3 w-3 rounded-full border-2 border-white ${(employee.status || "").toLowerCase() === "active" ? "bg-emerald-500" : "bg-slate-300"}`} />
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-bold text-slate-800 leading-tight">
                  {fullName}
                </h3>
                {employee.status && (
                  <StatusBadge status={employee.status} />
                )}
              </div>
              <p className="mt-0.5 text-xs text-slate-400 font-medium">
                {employee.employee_id || "EMP-XXXX"}
              </p>
              <p className="mt-1.5 text-sm font-semibold text-slate-700 leading-none">
                {employee.designation || "Employee"}
              </p>
              <p className="mt-1 text-xs text-slate-400">
                {employee.department_name || "General"}
              </p>
            </div>
          </div>
        )}

        {/* TABS */}

        <div className="shrink-0 border-b border-slate-200 px-5">
          <div className="flex overflow-x-auto">
            {tabs.map((tab) => {
              const isActive =
                activeTab === tab.id;

              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() =>
                    setActiveTab(tab.id)
                  }
                  className={`
                    relative
                    shrink-0
                    px-3
                    py-3
                    text-sm
                    font-medium
                    transition-colors
                    ${
                      isActive
                        ? "text-[#147db5]"
                        : "text-slate-500 hover:text-slate-800"
                    }
                  `}
                >
                  {tab.label}

                  {isActive && (
                    <span
                      className="
                        absolute
                        bottom-0
                        left-2
                        right-2
                        h-0.5
                        rounded-full
                        bg-[#147db5]
                      "
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* TAB CONTENT */}

        <div className="min-h-0 flex-1 overflow-y-auto">

          {/* OVERVIEW */}

          {activeTab === "overview" && (
            <EmployeeProfileCard
              employee={employee}
            />
          )}

          {/* HISTORY */}

          {activeTab === "history" && (
            <div className="p-5">
              <HistoryTab
                history={
                  employeeHistory
                }
                employee={employee}
              />
            </div>
          )}

          {/* CONTRACTS */}

          {activeTab === "contracts" && (
            <div className="p-5">
              <ContractsTab
                contracts={
                  activeContractsList
                }
                employees={
                  employee
                    ? [employee]
                    : employees
                }
              />
            </div>
          )}

          {/* SCHEDULE */}

          {activeTab === "schedule" && (
            <div className="p-5">
              <ScheduleTab
                schedules={
                  activeSchedulesList
                }
                employees={
                  employee
                    ? [employee]
                    : employees
                }
              />
            </div>
          )}

        </div>

        {/* FOOTER */}

        <div className="shrink-0 border-t border-slate-200 bg-white px-5 py-4">
          <button
            type="button"
            onClick={() => {
              navigate(`/employment/profile/${employee.uuid || employee.employee_id}`, { state: { employee } });
              onClose?.();
            }}
            className="
              flex
              w-full
              items-center
              justify-center
              gap-2
              rounded-lg
              border
              border-slate-200
              px-4
              py-2.5
              text-sm
              font-medium
              text-slate-600
              transition
              hover:bg-slate-50
              hover:text-slate-900
            "
          >
            <ExternalLink
              size={16}
              strokeWidth={1.8}
            />

            <span>
              View Full Profile
            </span>
          </button>
        </div>

      </div>
    </RightDrawer>
  );
};

export default EmployeeDrawer;